<template>
  <div>
    <Breadcrumb :breadcrumb="breadcrumb"></Breadcrumb>

    <vue-element-loading :active="isLoading" spinner="spinner" />

    <h5 class="title is-5">{{ $t("devices.title") }}</h5>
    <div class="columns">
      <div class="column is-half">
        <button
          class="button"
          :class="{ 'is-loading': isCsvLoading }"
          @click="download"
        >
          <span class="icon is-small"> <i class="fas fa-save"></i> </span>
          <span>CSV Download</span>
        </button>
        <div class="field mt-3">
          <div class="control has-icons-left">
            <input
              class="input is-normal"
              type="text"
              placeholder="Search"
              v-model="searchQuery"
              @input="currentPage = 1"
            />
            <span class="icon is-small is-left">
              <i class="fas fa-search"></i>
            </span>
          </div>
        </div>
      </div>
    </div>

    <table class="table is-fullwidth">
      <thead>
        <tr class="has-text-centered">
          <th>{{ $t("devices.deviceId") }}</th>
          <th>{{ $t("projects.projectName") }}</th>
          <th>{{ $t("devices.deviceLocate") }}</th>
          <th>{{ $t("devices.aiLevel") }}</th>
          <th>{{ $t("devices.undetected") }}</th>
          <th>{{ $t("devices.battery") }}</th>
          <th>{{ $t("devices.antenna") }}</th>
          <th>{{ $t("devices.lastConnect") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, deviceId) in filteredLists"
          :key="deviceId"
          class="has-text-centered"
          :class="backgroundcolor(item.diffDay, item.hasError)"
        >
          <td>
            <span
              v-if="item.deviceType == 'ThermoCamera'"
              class="imsr-camera-icon"
              ><i class="fas fa-thermometer-three-quarters"></i
            ></span>
            <span
              v-if="item.deviceType == 'TrailCamera'"
              class="imsr-camera-icon"
              ><i class="fas fa-camera"></i
            ></span>
            {{ item.deviceId }}
          </td>
          <td>{{ item.projectName }}</td>
          <td>{{ item.deviceLocate }}</td>
          <td>
            <AI :aiLevel="item.aiLevel" :device="item.deviceType" />
          </td>
          <td>
            <Undetected
              :enabledUndetectedarea="item.enabledUndetectedarea"
              :device="item.deviceType"
            />
          </td>
          <td>
            <Battery
              :battery="item.battery"
              :antenna="item.antenna"
              :heartBeatError="item.heartBeatError"
            />
          </td>
          <td>
            <Antenna :antenna="item.antenna" />
          </td>
          <td>
            <span :data-tooltip="item.dateTime">
              <span v-if="item.diffDay != ''">{{ item.diffDay }}</span>
              <span v-else>{{ $t("devices.notinoperation") }}</span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <nav
      v-if="isPager == true"
      class="pagination is-centered is-small"
      role="navigation"
      aria-label="pagination"
    >
      <paginate
        v-model="currentPage"
        :page-count="getPageCount"
        :click-handler="clickCallback"
        :page-range="3"
        :margin-pages="2"
        :prev-text="'<'"
        :next-text="'>'"
        :page-class="'page-item'"
        :container-class="'pagination-list'"
        :page-link-class="'pagination-link'"
      >
      </paginate>
    </nav>
  </div>
</template>
<script>
import Breadcrumb from "@/components/Breadcrumb";
import AI from "@/components/devices/AI";
import Antenna from "@/components/devices/Antenna";
import Battery from "@/components/devices/Battery";
import Undetected from "@/components/devices/Undetected";
import imsr from "@/imsr-api";
import imsrc from "@/imsr-common";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import Paginate from "vuejs-paginate-next";
import VueElementLoading from "vue-element-loading";

export default {
  components: {
    Breadcrumb,
    Battery,
    Antenna,
    AI,
    Undetected,
    paginate: Paginate,
    VueElementLoading,
  },
  setup() {
    const { t } = useI18n();
    const store = useStore();
    const router = useRouter();
    const deviceList = ref([]);
    const searchQuery = ref("");
    const currentPage = ref(1); // 現在のページ
    const perPage = ref(50); // 1ページあたりのアイテム数
    const deviceError = ref(false);
    const breadcrumb = ref([
      { title: "Admin", path: "#", isActive: true },
      { title: "Device Config", path: "#", isActive: true },
    ]);
    const isCsvLoading = ref(false);
    const isLoading = ref(false);
    const errorMessage = ref("");

    const getList = async () => {
      isLoading.value = true;
      try {
        const params = {
          tenantId: store.getters.tenantId,
        };
        const res = await imsr.imsrGetApi("/devices/allList", params);
        res.forEach((item) => {
          var diffDay = "";
          var dateTime = "";
          if (item.heartBeatUpdateTime) {
            diffDay = imsrc.dateDiff(item.heartBeatUpdateTime);
            dateTime = imsrc.dateFormat(item.heartBeatUpdateTime);
          }
          let hasError = false;
          if (item.deviceSetting === "JPEG" || item.heartBeatError) {
            deviceError.value = true;
            hasError = true;
          }
          deviceList.value.push({
            deviceId: item.deviceId,
            projectId: item.projectId,
            projectName: item.projectName.substr(0, 10),
            deviceType: item.deviceType,
            deviceLocate: item.deviceLocate.substr(0, 10),
            battery: item.battery,
            antenna: item.antenna,
            diffDay: diffDay,
            dateTime: dateTime,
            hasError: hasError,
            aiLevel: item.aiLevel,
            enabledUndetectedarea: item.enabledUndetectedarea,
            antennaRawData: item.antennaRawData,
            heartBeatError: item.heartBeatError,
          });
        });
        isLoading.value = false;
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };
    getList();

    const download = async () => {
      isCsvLoading.value = true;
      errorMessage.value = "";
      const params = {
        tenantId: store.getters.tenantId,
      };
      try {
        await imsr.csvDownload("/devices/csv", params, "devices");
      } catch (e) {
        errorMessage.value = t("download.errorDownload");
      } finally {
        isCsvLoading.value = false;
      }
    };

    const isPager = computed(() => {
      return filteredDevices.value.length > perPage.value;
    });

    // 検索クエリに基づいてユーザーリストをフィルタリング
    const filteredDevices = computed(() => {
      return deviceList.value.filter(
        (device) =>
          device.deviceId
            .toLowerCase()
            .includes(searchQuery.value.toLowerCase()) ||
          device.deviceLocate
            .toLowerCase()
            .includes(searchQuery.value.toLowerCase()) ||
          device.projectName
            .toLowerCase()
            .includes(searchQuery.value.toLowerCase())
      );
    });

    // ページネーションに基づいてリストを取得
    const filteredLists = computed(() => {
      let end = currentPage.value * perPage.value;
      let start = end - perPage.value;
      return filteredDevices.value.slice(start, end);
    });

    const getPageCount = computed(() => {
      return Math.ceil(filteredDevices.value.length / perPage.value);
    });

    const clickCallback = (pageNum) => {
      currentPage.value = Number(pageNum); //ページネーションで選択したページ数
    };

    return {
      deviceList,
      breadcrumb,
      deviceError,
      searchQuery,
      download,
      errorMessage,
      isPager,
      filteredLists,
      getPageCount,
      clickCallback,
      currentPage,
      isLoading,
      isCsvLoading,
    };
  },
  methods: {
    backgroundcolor(diffDay, hasError) {
      if (diffDay == "") {
        return "imsr-notinoperation";
      } else if (hasError) {
        return "imsr-danger";
      }
    },
  },
};
</script>
<style>
.imsr-danger {
  background-color: hsl(347, 90%, 96%);
}
.imsr-notinoperation {
  background-color: hsl(183, 90%, 96%);
}
.imsr-camera-icon {
  margin-right: 4px;
}
</style>
